import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid-pro'
import {
  styleConstants,
  Tag,
  TagScales,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import UserAvatarCell from 'src/components/DataGrid/cells/UserAvatarCell/UserAvatarCell'
import DataGrid, {
  DataGridRowHeight,
  handleCellClick,
} from 'src/components/DataGrid/DataGrid'
import NoPeople from 'src/components/NoPeople/NoPeople'
import { PeopleFiltersBody } from 'src/components/PeopleBoxAndLinks/PeopleBoxAndLinks'
import SvgOrganizationFilled from 'src/components/Svgs/SvgOrganizationFilled'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature, UserListModel } from 'src/models/user.models'
import Lnk, { connectedPush } from 'src/navigation/Lnk'
import {
  budgetsAllBudgetsUrl,
  cardsPhysicalCardsUrl,
  peopleMembersDetailUrl,
  virtualCardsAllCardsUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { InfiniteScrollProps } from 'src/types'
import { css } from 'styled-components'

interface PeopleMembersDataGridProps {
  people: UserListModel[]
  clientHeight?: number
  infiniteScrollProps?: InfiniteScrollProps<PeopleFiltersBody>
  filtersBody?: PeopleFiltersBody
  sortableProps: {
    setSort: (field: string) => void
    sortBy?: string
    sortDescending?: boolean
  }
}

interface PeopleMembersDataGridRow {
  id: string
  person: UserListModel
  index: number
}

enum sortValues {
  name = 'firstName',
  organizationRole = 'organizationRole',
}

const DEACTIVATED_ROW_CLASS = 'row--deactivated'

const PeopleMembersDataGrid: React.FC<PeopleMembersDataGridProps> = (props) => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const currentUser = useCurrentUser()

  const { ORGANIZATION_USER_STATISTICS: userStatsFeature } =
    useQueryUserFeatures([UserFeature.ORGANIZATION_USER_STATISTICS]).data || {}
  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: currentUser?.organizationId,
  })
  const departmentsEnabled = orgFeatureFlags?.departmentsEnabled === true

  const canViewFullPersonStats =
    userStatsFeature || currentUser?.hasOrgAdminPermissions

  const [sortModel, setSortModel] = useState<GridSortModel>([])

  useEffect(() => {
    if (sortModel?.length > 0) {
      let sortField: sortValues
      switch (sortModel?.[0]?.field) {
        case 'role':
          sortField = sortValues.organizationRole
          break
        case 'name':
        default:
          sortField = sortValues.name
          break
      }
      props.sortableProps?.setSort?.(sortField)
      analytics?.track(
        'people org member list - sort people - table header press',
        {
          screen: '/people/members',
          interaction: 'table header press',
          intent: 'sort people',
          sortField,
          sortDirection: props.sortableProps?.sortDescending ? 'DESC' : 'ASC',
        },
      )
    }
  }, [sortModel])

  const rows: PeopleMembersDataGridRow[] = props.people.map(
    (person, index) => ({
      id: person.id,
      person,
      index,
    }),
  )

  const statLink = (
    value: number | undefined,
    pathname: string,
    search: string,
    ariaLabelSource: string,
    params: GridCellParams<PeopleMembersDataGridRow>,
  ): React.ReactElement => {
    if (value && value > 0) {
      return (
        <DataGridCellWrapper params={params}>
          <Lnk
            css={css`
              color: ${themeConstants.colors.cta};
              text-decoration: underline;
            `}
            to={{ pathname, search, hash: '', state: {} }}
            ariaLabelSource={ariaLabelSource}
          >
            {value.toLocaleString(locale)}
          </Lnk>
        </DataGridCellWrapper>
      )
    }
    return <DataGridCell text={'0'} params={params} />
  }

  const columns: Array<GridColDef<PeopleMembersDataGridRow>> = [
    {
      field: 'name',
      headerName: translate('Name'),
      flex: 2,
      minWidth: 220,
      // sorting temporarily disabled
      sortable: false,
      renderCell: (params): React.ReactElement => (
        <UserAvatarCell
          user={params.row.person}
          subtext={params.row.person.email}
          disabled={params.row.person.deactivated}
          params={params}
        />
      ),
    },
  ]

  if (canViewFullPersonStats) {
    columns.push({
      field: 'status',
      headerName: translate('Status'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params): React.ReactElement => (
        <DataGridCell
          text={
            params.row.person.deactivated
              ? translate('Deactivated')
              : translate('Active')
          }
          params={params}
        />
      ),
    })
  }

  columns.push({
    field: 'role',
    headerName: translate('Role'),
    flex: 1,
    minWidth: 150,
    // sorting temporarily disabled
    sortable: false,
    renderCell: (params): React.ReactElement => {
      const person = params.row.person
      if (person.deactivated) {
        return <DataGridCell params={params} />
      }
      return (
        <DataGridCellWrapper params={params}>
          <Tag
            text={translate(person.orgRoleDisplayTagTitle)}
            icon={
              person.hasOrgOwnerPermissions ? (
                <SvgOrganizationFilled style={TagScales.medium} />
              ) : undefined
            }
          />
        </DataGridCellWrapper>
      )
    },
  })

  if (departmentsEnabled && canViewFullPersonStats) {
    columns.push({
      field: 'team',
      headerName: translate('Team'),
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params): React.ReactElement => (
        <DataGridCell
          text={
            params.row.person.deactivated
              ? undefined
              : params.row.person.department?.name
          }
          params={params}
        />
      ),
    })
  }

  if (canViewFullPersonStats) {
    columns.push({
      field: 'accounts',
      headerName: translate('Accounts'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params): React.ReactElement => {
        const person = params.row.person
        if (person.deactivated) {
          return <DataGridCell params={params} />
        }
        return statLink(
          person.accounts,
          cardsPhysicalCardsUrl(),
          // No status filter: person.accounts is a total across all statuses,
          // so the destination list should show all of the user's cards to
          // match the count shown in the members table.
          `?user=${person.id}`,
          'accounts',
          params,
        )
      },
    })

    if (hasVirtualCardFeatures) {
      columns.push({
        field: 'budgets',
        headerName: translate('Budgets'),
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params): React.ReactElement => {
          const person = params.row.person
          if (person.deactivated) {
            return <DataGridCell params={params} />
          }
          return statLink(
            person.budgets,
            budgetsAllBudgetsUrl(),
            `?statuses=ACTIVE&users=${person.id}`,
            'budgets',
            params,
          )
        },
      })

      columns.push({
        field: 'virtualCards',
        headerName: translate('Virtual Cards'),
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params): React.ReactElement => {
          const person = params.row.person
          if (person.deactivated) {
            return <DataGridCell params={params} />
          }
          return statLink(
            person.virtualCards,
            virtualCardsAllCardsUrl(),
            `?statuses=ACTIVE&recipients=${person.id}`,
            'virtual cards',
            params,
          )
        },
      })
    }
  }

  columns.push(chevronColumnDef)

  return (
    <DataGrid
      height={props.clientHeight}
      rows={rows}
      columns={columns}
      filtersBody={props.filtersBody || {}}
      infiniteScrollProps={props.infiniteScrollProps}
      rowHeight={DataGridRowHeight.Comfy}
      sortModel={sortModel}
      onSortModelChange={(newSortModel): void => setSortModel(newSortModel)}
      disableColumnFilter
      getRowClassName={(params): string =>
        params.row.person?.deactivated ? DEACTIVATED_ROW_CLASS : ''
      }
      slots={{
        noRowsOverlay: (): React.ReactElement => (
          <div
            css={css`
              display: flex;
              width: 100%;
              height: 100%;
              align-items: center;
              justify-content: center;
            `}
          >
            <NoPeople
              titleText={translate('No one matched your search.')}
              topSubtitleText={translate(
                'Check the keyword you typed for any errors.',
              )}
            />
          </div>
        ),
      }}
      sx={{
        [`& .MuiDataGrid-row.${DEACTIVATED_ROW_CLASS}, & .MuiDataGrid-row.${DEACTIVATED_ROW_CLASS} .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.${DEACTIVATED_ROW_CLASS} .MuiDataGrid-cell--pinnedRight`]:
          {
            backgroundColor: `${styleConstants.colors.gray_light}80`,
          },
      }}
      onCellClick={handleCellClick}
      onRowClick={(row): void => {
        const person = row.row.person
        dispatch(
          connectedPush({
            pathname: peopleMembersDetailUrl(person.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
        analytics?.track(
          'people org member list - open user detail - list item press',
          {
            screen: '/people/members',
            interaction: 'list item press',
            intent: 'open user detail',
            viewedUserId: person.id,
          },
        )
      }}
    />
  )
}

export default PeopleMembersDataGrid
